import { AfterContentChecked, Component, ElementRef, HostListener, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, UrlSegment } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { LoginService } from './core/services/login.service';
import { SettingsService } from './core/services/settings.service';
import { mimesysConfig, Settings } from './core/models/settings.model';
import { User } from './core/models/user.model';
import * as utils from './shared/helpers/utils';
import { GazeMapService } from './core/services/gaze-map.service';
import { distinctUntilChanged, filter, Subscription } from 'rxjs';
import { ChangeDetectorRef } from '@angular/core';
import { EntityService } from './core/services/entity.service';
import { ConfigService } from './core/services/config.service';
import { DdbbUtils } from './shared/helpers/utils';
import { UnsavedChangesGuard } from './core/guards/unsavedChanges.guard';
import { DashboardApps, Groups } from 'src/app/core/models/group.model';

@Component({
  selector: 'hbo-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, AfterContentChecked, OnDestroy {

  title = 'backoffice-front';
  currentPage = '';
  settings!: Settings;
  user!: User;
  availableLanguages: string[] = [];

  showCopy!: boolean;
  showBack!: string | null;
  previousUrl!: string;
  currentUrl!: string;
  data_copied!: any;

  gazeMapLstnr: Subscription = new Subscription;
  routeQueryParamsLstnr: Subscription = new Subscription;
  settingsStoreLstnr: Subscription = new Subscription;
  userStoreLstnr: Subscription = new Subscription;
  routerLstnr: Subscription = new Subscription;

  groups: Groups = new Groups;

  mimesysAvailable: boolean = false;
  fitAvailable: boolean = false;
  depict360Available: boolean = false;

  // Loaded by checkLicense. The sidebar entry has to answer with the SAME rule as the page,
  // or it offers a dashboard that then bounces the user back to /entities.
  private entityApps: DashboardApps | null = null;

  get showDashboard(): boolean {
    return (this.user?.roleId === 1 && this.user?.profileId === 3)
      || (this.user?.roleId === 4 && !!this.entityApps && this.groups.showDashboard(this.entityApps));
  }

  showContent: boolean = false;

  //regular progress entities progressive loading
  initializedLoadingEntities: boolean = false;
  phases: number = 0;
  phasesCompleted: number = 0;
  errorLoading = false;
  totalEntities: string = '';
  modalRendered: boolean = false;

  entitiesByPage: number = 10000;

  adminTools: boolean = false;

  constructor(
    private cdref: ChangeDetectorRef,
    public router: Router,
    public route: ActivatedRoute,
    private renderer: Renderer2,
    private el: ElementRef,
    public translateService: TranslateService,
    public loginService: LoginService,
    public settingsService: SettingsService,
    private gazeMapService: GazeMapService,
    private entityService: EntityService,
    public configService: ConfigService,
    private ddbbUtils: DdbbUtils,
    private unsavedChangesGuard: UnsavedChangesGuard
  ) {
    if (JSON.parse(localStorage.getItem('debug')!) === true && JSON.parse(localStorage.getItem('forceExpiredToken')!) === true) {
      utils.setExpiredToken();
    }
    this.gazeMapLstnr = this.gazeMapService.currentGazeMapConfigSubject.subscribe(data => {
      data.testId
        ? (this.showCopy = true, this.data_copied = data.testId)
        : this.showCopy = false;
    });
    if (utils.localStorageGetItem('user')) {
    } else {
      this.showContent = true;
    };
  }

  ngOnInit(): void {
    this.initializeLanguage();
  }

  private initializeLanguage() {
    const browserLang = this.translateService.getBrowserLang();
    const browserCulture = navigator.language || navigator.languages?.[0];
    const systemLanguage = (navigator as any).systemLanguage || (navigator as any).userLanguage;

    const supportedLanguages = [
      'en-GB', 'es-ES', 'en-AU', 'de', 'pl', 'fr', 'pt', 'pt-BR',
      'it', 'he', 'ar', 'sl', 'bg', 'zh', 'zh-TW', 'nl', 'tr', 'lt', 'th',
      'el', 'ko', 'ms', 'ru', 'br', 'hu', 'ro'
    ];

    // Language mapping for better Safari compatibility
    const languageMap: { [key: string]: string } = {
      'en': 'en-GB',
      'en-US': 'en-GB',
      'es': 'es-ES',
      'pt': 'pt',
      'zh': 'zh',
      'zh-CN': 'zh',
      'zh-Hans': 'zh',
      'zh-Hant': 'zh-TW'
    };

    let defaultLang = 'en-GB'; // fallback

    // Try multiple detection methods
    const candidateLanguages = [
      browserCulture,
      browserLang,
      systemLanguage,
      navigator.languages?.[0]
    ].filter(Boolean);

    for (const candidate of candidateLanguages) {
      if (!candidate) continue;

      // Exact match
      if (supportedLanguages.includes(candidate)) {
        defaultLang = candidate;
        break;
      }

      // Mapped match
      if (languageMap[candidate]) {
        defaultLang = languageMap[candidate];
        break;
      }

      // Language code match (e.g., 'es' -> 'es-ES')
      const langCode = candidate.split('-')[0];
      const languageVariant = supportedLanguages.find(lang => lang.startsWith(langCode + '-'));
      if (languageVariant) {
        defaultLang = languageVariant;
        break;
      }

      // Direct language code match
      if (supportedLanguages.includes(langCode)) {
        defaultLang = langCode;
        break;
      }
    }

    try {
      this.translateService.setDefaultLang(defaultLang);
      this.translateService.use(defaultLang);
    } catch (error) {
      console.error('Error setting language:', error);
      // Emergency fallback
      this.translateService.setDefaultLang('en-GB');
      this.translateService.use('en-GB');
    }
  }

  onCompleteRendered(): void {
    this.configService.setConfigParamsValue('debug', JSON.parse(localStorage.getItem('debug')!) === true);
    this.sidebarCollapse();
    this.routeQueryParamsLstnr = this.route.queryParams.subscribe(queryParams => {
      const loginToken = queryParams['loginToken']
      // si lleva login token la url usa el login token
      if (loginToken) {
        this.settingsService.getByLoginToken(loginToken).subscribe(res => {
          this.settingsService.settingsStore.next(res);
          this.configService.setConfigParamsValue('userSettings', res);
        });
        this.showContent = true;
        // en el caso de estar logado lo hace por user
      } else if (utils.localStorageGetItem('user')) {
        if (!this.configService.currentConfigValue.refreshingToken) {
          this.initializedLoadingEntities = false;
          if (location.href.includes('login?loginToken=')) {
            this.router.navigate(['/login']);
          }
          this.settingsService.get(utils.localStorageGetItem('user').entity.id, 0).subscribe(settings => {
            utils.localStorageSetItem('loginToken', settings?.loginToken);
            this.settingsService.settingsStore.next(settings);
            this.configService.setConfigParamsValue('userSettings', settings);
          });
        } else {
          this.configService.setConfigParamsValue('refreshingToken', false);
        }
      };
    });

    this.settingsStoreLstnr = this.settingsService.settingsStore.pipe(distinctUntilChanged()).subscribe(res => {
      if (res && Object.keys(res).length > 0) {
        const isPreview = res['isPreview'] ?? false;
        delete res['isPreview']; // Remove the preview flag from the settings
        this.settings = res;
        this.setSettings();
        if (!isPreview) {
          this.checkLicense(!this.router.url.includes('/gaze-maps/'));
        }
        document.dispatchEvent(new Event('DOMContentLoaded', { bubbles: true, cancelable: true }));
        if (!this.initializedLoadingEntities || this.configService.currentConfigValue.childEntities!.length) {
          this.onModalEntitiesRendered();
        }
      }

    });

    this.userStoreLstnr = this.loginService.userStore.subscribe(res => {
      if (res.fullname) {
        this.user = res;
      } else if (utils.localStorageGetItem('user')) {
        this.loginService.userStore.next(utils.localStorageGetItem('user') as User);
      }
    });

    this.routerLstnr = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        this.previousUrl = this.currentUrl;
        this.currentUrl = event.urlAfterRedirects;
        this.configService.setConfigPathsValue(event.url);
      })


  }

  ngOnDestroy(): void {
    this.gazeMapLstnr.unsubscribe();
    this.routeQueryParamsLstnr.unsubscribe();
    this.settingsStoreLstnr.unsubscribe();
    this.userStoreLstnr.unsubscribe();
    this.routerLstnr.unsubscribe();
  }

  ngAfterContentChecked(): void {
    this.cdref.detectChanges();
    if (this.router.url.includes('dashboard')) {
      this.showBack = null;
      this.currentPage = this.translateService.instant('COMMON.DASHBOARD');
    } else if (this.router.url.includes('fits')) {
      this.showBack = null;
      this.currentPage = this.translateService.instant('COMMON.FIT');
    } else if (this.router.url.includes('gaze-maps')) {
      if (this.router.url.split('/').length === 3) {
        this.showBack = '/gaze-maps';
        this.currentPage = this.translateService.instant('COMMON.TESTID') + (this.data_copied ?? '');
      } else {
        this.showBack = null;
        this.currentPage = this.translateService.instant('COMMON.GAZE_MAPS');
      }
    } else if (this.router.url.includes('scanns') || this.router.url.includes('orders')) {
      this.currentPage = this.translateService.instant('COMMON.MADE4U');
    } else if (this.router.url.includes('admin-tools')) {
      this.showBack = null;
      this.currentPage = this.translateService.instant('COMMON.ADMIN_TOOLS');
    } else if (this.router.url.includes('settings') && !this.router.url.includes('user/settings')) {
      this.showBack = null;
      this.currentPage = this.translateService.instant('COMMON.SETTINGS');
    } else if (this.router.url.includes('entities')) {
      this.showBack = null;
      this.currentPage = this.translateService.instant('COMMON.ENTITIES');
    } else if (this.router.url.includes('devices')) {
      this.showBack = null;
      this.currentPage = this.translateService.instant('COMMON.DEVICES');
    } else if (this.router.url.includes('licenses')) {
      if (this.router.url.includes('licenses/packages/create')) {
        this.showBack = '/licenses/packages';
        this.currentPage = this.translateService.instant('COMMON.NEW_PACKAGE');
      } else if (this.router.url.includes('licenses/packages/edit')) {
        this.showBack = '/licenses/packages';
        this.currentPage = this.translateService.instant('COMMON.PACKAGE_LICENSE_DETAILS');
      } else {
        this.showBack = null;
        this.currentPage = this.translateService.instant('COMMON.LICENSES');
      }
    } else if (this.router.url.includes('user') && !this.router.url.includes('user/settings')) {
      this.showBack = null;
      this.currentPage = this.translateService.instant('COMMON.USERS');
    } else if (this.router.url.includes('user/settings')) {
      this.showBack = this.previousUrl;
      this.currentPage = this.translateService.instant('COMMON.USER_SETTINGS');
    } else if (this.router.url.includes('analytics')) {
      if (this.router.url.includes('analytics/fit')) {
        this.showBack = null;
        this.currentPage = this.translateService.instant('Analytics Fit');
      } else if (this.router.url.includes('analytics/vrresults')) {
        this.showBack = null;
        this.currentPage = this.translateService.instant('Analytics VR Results');
      } else {
        this.showBack = null;
        this.currentPage = this.translateService.instant('Analytics Packages');
      }
    } 
    }

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.sidebarCollapse();
  }

  sidebarCollapse() {
    if (window.innerWidth < 1200) {
      document.querySelector('.sidebar-mini')?.classList.add('sidebar-collapse')
    } else {
      document.querySelector('.sidebar-mini')?.classList.remove('sidebar-collapse')
    }
  }

  setSettings() {
    if (this.settings) {
      const currentLang = utils.localStorageGetItem('currentLang').length ? utils.localStorageGetItem('currentLang') : this.settings.language;
      if (currentLang) {
        this.changeLanguage(currentLang, false)
      }

      // Opciones de Sidebar
      const sidebar = document.querySelector<HTMLElement>('.app-sidebar');
      if ((this.settings.layout.sidebar.background === 'color' && this.settings.layout.sidebar.backgroundColor)
        || (this.settings.layout.sidebar.background === 'image' && !this.settings.layout.sidebar.backgroundImage.length)) {
        sidebar?.style.setProperty('--sidebarBackgroundColor', this.settings.layout.sidebar.backgroundColor);
        sidebar?.style.setProperty('--sidebarImage', '');
      }
      else if (this.settings.layout.sidebar.background === 'image' && this.settings.layout.sidebar.backgroundImage) {
        sidebar?.style.setProperty('--sidebarImage', 'url(' + this.settings.layout.sidebar.backgroundImage + ')');
        sidebar?.style.setProperty('--sidebarBackgroundColor', '');
      }

      if (this.settings.layout.sidebar.color === 'white') {
        sidebar?.style.setProperty('--lte-sidebar-color', 'white')
        sidebar?.style.setProperty('--lte-sidebar-hover-bg', 'white')
        sidebar?.style.setProperty('--lte-sidebar-hover-color', 'black')
        sidebar?.style.setProperty('--lte-sidebar-submenu-active-color', 'white')
        sidebar?.style.setProperty('--lte-sidebar-submenu-hover-color', 'white')
      }
      else if (this.settings.layout.sidebar.color === 'black') {
        sidebar?.style.setProperty('--lte-sidebar-color', 'black')
        sidebar?.style.setProperty('--lte-sidebar-hover-bg', 'black')
        sidebar?.style.setProperty('--lte-sidebar-hover-color', 'white')
        sidebar?.style.setProperty('--lte-sidebar-submenu-active-color', 'black')
        sidebar?.style.setProperty('--lte-sidebar-submenu-hover-color', 'black')
      }

      // logos
      sidebar?.style.setProperty('--logo4', 'url(' + this.settings.logo[4]?.image + ')');
      sidebar?.style.setProperty('--logo5', 'url(' + this.settings.logo[5]?.image + ')');
      sidebar?.style.setProperty('--logo6', 'url(' + this.settings.logo[6]?.image + ')');
    }
  }

  changeLanguage(lang: string, persistence = true) {
    this.translateService.setDefaultLang(lang);
    this.translateService.addLangs(['he']);
    this.translateService.use(lang);
    if (persistence) {
      utils.localStorageSetItem('currentLang', lang);
    }

    this.availableLanguages = utils.backoffice_languages
      .filter(langObj => langObj.value !== lang)
      .map(langObj => langObj.value)

  }

  public notify(event: string): void {
    let message = `'${event}' has been copied to clipboard`
    console.log(message);
  }

  public stamp(): string {
    let now = new Date();
    let hours = now.getHours();
    let minutes = now.getMinutes();
    let seconds = now.getSeconds();
    return `time (${hours}:${minutes}:${seconds})`;
  }

  private checkLicense(redirect = true): void {
    if (utils.localStorageGetItem('user') && this.user?.roleId !== 1) {
      if (this.user) this.entityService.getEntityApps(this.user.entityId).subscribe(res => {
        this.mimesysAvailable = this.user.roleId == 1 || (this.user.roleId != 1 && res.appsIds.some((appId: number) => this.groups.mimesys.includes(appId)));
        this.fitAvailable = this.user.roleId == 1 || (this.user.roleId != 1 && this.getFitAvailable(res.appsIds));
        this.entityApps = res;
        // The standalone product, not the licence: a Fit Depict entity carries app 7 bundled
        // with Fit and gets no dashboard of its own.
        this.depict360Available = (res.standaloneAppsIds ?? []).includes(7);
        const logged = utils.localStorageGetItem('logged')
        if (utils.localStorageGetItem('user')) utils.localStorageSetItem('logged', true)
        switch (this.user?.roleId) {
          case 1:
          case 2:
          case 3:
            redirect ? this.router.navigate(['/entities']) : null;
            break;
          case 4:
            // Runs after authGuard and navigates on its own, so the dashboard has to be
            // checked here too or this overrides the guard's redirect.
            if (this.showDashboard) redirect ? this.router.navigate(['/dashboard']) : null;
            else if (this.mimesysAvailable) redirect ? this.router.navigate(['/gaze-maps']) : null;
            else if (this.fitAvailable && !this.mimesysAvailable) redirect ? this.router.navigate(['/fits']) : null;
            else if (!this.fitAvailable && !this.mimesysAvailable) redirect ? this.router.navigate(['/entities']) : null;
            break;
          default:
            break;
        }
      });
    } else {
      this.mimesysAvailable = true;
      this.fitAvailable = true;
      this.depict360Available = true;
      const logged = utils.localStorageGetItem('logged')
      if (!logged || !JSON.parse(logged!)) {
        switch (this.user?.roleId) {
          case 1:
          case 2:
          case 3:
            this.router.navigate(['/entities']);
            break;
          case 4:
            if (this.mimesysAvailable) this.router.navigate(['/gaze-maps'])
            else if (this.fitAvailable && !this.mimesysAvailable) this.router.navigate(['/fits'])
            else if (!this.fitAvailable && !this.mimesysAvailable) this.router.navigate(['/entities'])
            break;
          default:
            break;
        }
        if (utils.localStorageGetItem('user')) utils.localStorageSetItem('logged', true)
      }
    }
  }

  getLicenseName(_apps: number[]): string {
    let appsAbrev: { id: number, typeId: number, abrev: string }[] = mimesysConfig.config['appsAbrev'];
    let packageTypes: { id: number, value: string, apps: string[], appsTypeId: number[], preview: string, layout: string, layoutImgSubmenu: string, wallLayout: string, pdfLayout: string }[] = mimesysConfig.config['packageTypes'];
    let appsIds: { id: number, typeId: number, abrev: string }[] = [];
    _apps.map(app => appsAbrev.find(item => {
      if (item.typeId == app) appsIds.push(item)
    }))
    appsIds = appsIds.filter((abrev, index, array) => array.indexOf(abrev) === index);
    let appsAbrevs = appsIds.map(({ abrev }) => abrev);
    let name = '';
    packageTypes.filter(item => {
      let difference = item.apps.filter(x => !appsAbrevs.includes(x));
      !difference?.length ? name = item.value : null
    });
    console.log('name', name);
    return name
  }
  getFitAvailable(_apps: number[]): boolean {
    let appsIds: number[] = _apps;
    if (appsIds.includes(5)) {
      return true
    } else {
      return false
    }
  }

  logOut = () => {
    //because of the tree view bootstrap rendering when logout it is not triggered de routing change
    //while view app.component, so when a side bar item is selected it keeps active after login again
    //but we want only dashboard active initially
    this.initializedLoadingEntities = false;
    this.phases = 0;
    this.phasesCompleted = 0;
    this.totalEntities = '';
    this.removeClassFromAllElements('menu-open-auto');
    this.loginService.logOut();
  }

  removeClassFromAllElements(className: string): void {
    let elements = this.el.nativeElement.querySelectorAll(`.${className}`);
    elements.forEach((element: HTMLElement) => {
      this.renderer.removeClass(element, className);
    });
    elements = this.el.nativeElement.querySelectorAll(`.nav-treeview`);
    elements.forEach((element: HTMLElement) => {
      element.style.display = 'none'
      //this.renderer.removeStyle(element,'display');
    });

  }

  getEntitiesByPages = (_entityId: number, _page: number, _perPage: number, _action?: number) => {
    this.ddbbUtils.getEntityChilds(_entityId, _page, _perPage).then(async result => {
      if (_page === 1) {
        //if (result.total > _perPage) this.ddbbUtils.forceModal('#regularProgressModal', false);
        this.configService.setConfigParamsValue('childEntities', result.data);
        this.phases = Math.ceil(result.total / _perPage);
        this.totalEntities = `${result.total.toString()} ${this.translateService.instant('COMMON.ENTITIES')}`;
      } else {
        let concatanate = this.configService.currentConfigValue.childEntities!.concat(result.data)
        this.configService.setConfigParamsValue('childEntities', concatanate);
      }
      if (_page === this.phases) {
        this.phasesCompleted = _page;
        switch (_action) {
          default:
            await this.ddbbUtils.forceModal('#regularProgressModal', true);
            this.showContent = true;
            localStorage.removeItem('mimesysFilter')
            localStorage.removeItem('fitFilter')
            break;
        }
      } else {
        this.phasesCompleted = _page;
        this.getEntitiesByPages(_entityId, _page + 1, _perPage);
      }
    })
  }

  onModalEntitiesRendered = () => {
    if (!this.initializedLoadingEntities && utils.localStorageGetItem('user')) {
      this.modalRendered = true;
      this.ddbbUtils.forceModal('#regularProgressModal', false);
      this.initializedLoadingEntities = true;
      this.entitiesByPage = Number(utils.localStorageGetItem('entitiesByPage')) || this.entitiesByPage;
      this.getEntitiesByPages(utils.localStorageGetItem('user').entityId, 1, this.entitiesByPage);
    }
  }

  onModalEntitiesRendered2 = () => {
    this.onCompleteRendered();
  }

  onModalRefreshToken = () => {

  }

  refreshEntities = (e: Event) => {
    this.entitiesByPage = Number(utils.localStorageGetItem('entitiesByPage')) || this.entitiesByPage;
    this.getEntitiesByPages(utils.localStorageGetItem('user').entityId, 1, this.entitiesByPage);
  }

  toogleAdminTools = () => {
    this.adminTools = !this.adminTools;
  }

  callbackAdminTools = (e: any) => {
    this.adminTools = false;
  }

  refreshLogin = (e: any) => {
    const auth = utils?.localStorageGetItem('auth');
    this.configService.setConfigParamsValue('refreshingToken', true);
    this.loginService.refresh(auth.refreshToken).subscribe(res => {
      this.configService.setConfigParamsValue('refreshingToken', false);
      utils.localStorageSetItem('auth', { 'accessToken': res.access_token, 'refreshToken': res.refresh_token });
      utils.toogleGenericModal('#confirmationModal-expiredToken', true);
      if (res.access_token) { location.reload(); } else { this.loginService.logOut(); }
    }, error => {
      this.configService.setConfigParamsValue('refreshingToken', false);
      this.loginService.logOut();
    })
  }

  cancelRefreshLogin = (e: any, action: string) => {
    switch (action) {
      case 'cancel':
        this.loginService.logOut();
        break;
      default:
        break;
    }
  }

  refresh = (e: any) => {
    location.reload();
  }

  continue = (e: any) => {
    utils.toogleGenericModal('#confirmationModal-commonMessage', false);
  }

  continueNoSaveRef = (e: any) => {
    this.unsavedChangesGuard.confirmNavigation();
  }

  saveRefAndContinue = (e: any) => {
    this.unsavedChangesGuard.saveRefAndConfirmNavigation();
  }

}
